/**
 * FinancialReport — Configurable financial report with grouping, filtering,
 * subtotals, grand total, budget overlay, drill-down, and CSV export.
 *
 * Requirements: 20.1-20.11
 */

import { useState, useEffect, useCallback } from 'react';
import {
  reportService,
  FinancialReportParams,
  FinancialReportResult,
  FinancialReportRow,
} from '@/services/reportService';

const GROUP_OPTIONS = [
  { value: 'vendor', label: 'Vendor' },
  { value: 'glCode', label: 'GL Code' },
  { value: 'budgetCategory', label: 'Budget Category' },
  { value: 'departmentScope', label: 'Department' },
  { value: 'site', label: 'Site' },
  { value: 'period', label: 'Time Period' },
] as const;

const PERIOD_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
] as const;

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function FinancialReport() {
  const [groupBy, setGroupBy] = useState<string[]>(['vendor']);
  const [period, setPeriod] = useState('');
  const [glCode, setGlCode] = useState('');
  const [budgetCategory, setBudgetCategory] = useState('');
  const [departmentScope, setDepartmentScope] = useState('');
  const [siteNumber, setSiteNumber] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeBudgetOverlay, setIncludeBudgetOverlay] = useState(false);

  const [result, setResult] = useState<FinancialReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<{ glCodes: string[]; budgetCategories: string[]; departments: string[]; sites: Array<{ siteNumber: string; name: string }> } | null>(null);

  // Load filter options on mount
  useEffect(() => {
    fetch('/api/reports/filter-options', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  const buildParams = useCallback((): FinancialReportParams => ({
    groupBy: groupBy.join(','),
    period: period as FinancialReportParams['period'] || undefined,
    glCode: glCode || undefined,
    budgetCategory: budgetCategory || undefined,
    departmentScope: departmentScope || undefined,
    siteNumber: siteNumber || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    includeBudgetOverlay: includeBudgetOverlay || undefined,
  }), [groupBy, period, glCode, budgetCategory, departmentScope, siteNumber, dateFrom, dateTo, includeBudgetOverlay]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.getFinancialReport(buildParams());
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleGroupToggle = (value: string) => {
    setGroupBy(prev =>
      prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value],
    );
  };

  const handleExport = () => {
    const url = reportService.getFinancialExportUrl(buildParams());
    window.open(url, '_blank');
  };

  return (
    <div >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-text-primary">Financial Report</h1>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-baker-red text-white rounded-lg font-medium hover:bg-baker-red/90 hover:bg-baker-red/90"
          aria-label="Export report to CSV"
        >
          Export CSV
        </button>
      </div>

      {/* Grouping controls */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Group By</label>
        <div className="flex flex-wrap gap-2">
          {GROUP_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleGroupToggle(opt.value)}
              className={`px-3 py-1 rounded border text-sm ${
                groupBy.includes(opt.value)
                  ? 'bg-blue-100 border-blue-400 text-blue-800'
                  : 'bg-white border-gray-300 text-gray-600'
              }`}
              aria-pressed={groupBy.includes(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label htmlFor="periodFilter" className="block text-sm font-medium mb-1">Period</label>
          <select
            id="periodFilter"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="glCodeFilter" className="block text-sm font-medium mb-1">GL Code</label>
          <select
            id="glCodeFilter"
            value={glCode}
            onChange={e => setGlCode(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          >
            <option value="">All</option>
            {filterOptions?.glCodes.map(gc => <option key={gc} value={gc}>{gc}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="categoryFilter" className="block text-sm font-medium mb-1">Budget Category</label>
          <select
            id="categoryFilter"
            value={budgetCategory}
            onChange={e => setBudgetCategory(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          >
            <option value="">All</option>
            {filterOptions?.budgetCategories.map(bc => <option key={bc} value={bc}>{bc}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="deptFilter" className="block text-sm font-medium mb-1">Department</label>
          <select
            id="deptFilter"
            value={departmentScope}
            onChange={e => setDepartmentScope(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          >
            <option value="">All</option>
            {filterOptions?.departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="siteFilter" className="block text-sm font-medium mb-1">Site</label>
          <select
            id="siteFilter"
            value={siteNumber}
            onChange={e => setSiteNumber(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          >
            <option value="">All Sites</option>
            {filterOptions?.sites.map(s => <option key={s.siteNumber} value={s.siteNumber}>{s.siteNumber} — {s.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="dateFromFilter" className="block text-sm font-medium mb-1">Date From</label>
          <input
            id="dateFromFilter"
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          />
        </div>
        <div>
          <label htmlFor="dateToFilter" className="block text-sm font-medium mb-1">Date To</label>
          <input
            id="dateToFilter"
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-full"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeBudgetOverlay}
              onChange={e => setIncludeBudgetOverlay(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm font-medium">Budget Overlay</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded" role="alert">
          {error}
        </div>
      )}

      {loading && <div className="text-slate-400">Loading report...</div>}

      {result && !loading && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Grand Total (Contract)" value={formatCurrency(result.grandTotal)} />
            <SummaryCard label="Grand Total (Invoiced)" value={formatCurrency(result.grandTotalInvoiced)} />
            {Object.entries(result.subtotals).slice(0, 2).map(([key, val]) => (
              <SummaryCard key={key} label={`Subtotal: ${key}`} value={formatCurrency(val)} />
            ))}
          </div>

          {/* Data grid */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-page">
                  {groupBy.includes('vendor') && <th className="border px-3 py-2 text-left">Vendor</th>}
                  {groupBy.includes('glCode') && <th className="border px-3 py-2 text-left">GL Code</th>}
                  {groupBy.includes('budgetCategory') && <th className="border px-3 py-2 text-left">Budget Category</th>}
                  {groupBy.includes('departmentScope') && <th className="border px-3 py-2 text-left">Department</th>}
                  {groupBy.includes('site') && <th className="border px-3 py-2 text-left">Site</th>}
                  {groupBy.includes('period') && <th className="border px-3 py-2 text-left">Period</th>}
                  <th className="border px-3 py-2 text-right">Contract Spend</th>
                  <th className="border px-3 py-2 text-right">Invoiced Spend</th>
                  {includeBudgetOverlay && (
                    <>
                      <th className="border px-3 py-2 text-right">Budgeted</th>
                      <th className="border px-3 py-2 text-right">Variance ($)</th>
                      <th className="border px-3 py-2 text-right">Variance (%)</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row: FinancialReportRow, idx: number) => (
                  <tr key={idx} className="hover:bg-page">
                    {groupBy.includes('vendor') && (
                      <td className="border px-3 py-2">
                        <a href={`/vendors?search=${encodeURIComponent(row.vendor ?? '')}`} className="text-baker-red hover:underline">
                          {row.vendor ?? '—'}
                        </a>
                      </td>
                    )}
                    {groupBy.includes('glCode') && <td className="border px-3 py-2">{row.glCode ?? 'Unassigned'}</td>}
                    {groupBy.includes('budgetCategory') && <td className="border px-3 py-2">{row.budgetCategory ?? '—'}</td>}
                    {groupBy.includes('departmentScope') && <td className="border px-3 py-2">{row.departmentScope ?? '—'}</td>}
                    {groupBy.includes('site') && (
                      <td className="border px-3 py-2">
                        <a href={`/sites/${row.siteNumber}`} className="text-baker-red hover:underline">
                          {row.siteNumber ?? '—'}
                        </a>
                      </td>
                    )}
                    {groupBy.includes('period') && <td className="border px-3 py-2">{row.period ?? '—'}</td>}
                    <td className="border px-3 py-2 text-right">{formatCurrency(row.contractSpend)}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(row.invoicedSpend)}</td>
                    {includeBudgetOverlay && (
                      <>
                        <td className="border px-3 py-2 text-right">{row.budgetedAmount !== undefined ? formatCurrency(row.budgetedAmount) : '—'}</td>
                        <td className={`border px-3 py-2 text-right ${(row.dollarVariance ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {row.dollarVariance !== undefined ? formatCurrency(row.dollarVariance) : '—'}
                        </td>
                        <td className={`border px-3 py-2 text-right ${(row.percentVariance ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercent(row.percentVariance)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {result.rows.length === 0 && (
                  <tr>
                    <td colSpan={20} className="border px-4 py-8 text-center text-slate-500">
                      No data found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Subtotals */}
          {Object.keys(result.subtotals).length > 0 && (
            <div className="mt-6">
              <h2 className="text-lg font-medium mb-3">Subtotals</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(result.subtotals).map(([key, val]) => (
                  <div key={key} className="bg-page border rounded p-3">
                    <div className="text-xs text-slate-500">{key}</div>
                    <div className="text-sm font-semibold">{formatCurrency(val)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white bg-white rounded-lg border border-border p-5">
      <div className="text-sm text-slate-500 mb-1">{label}</div>
      <div className="text-xl font-semibold text-gray-900">{value}</div>
    </div>
  );
}
